// Aliase + Legacy-Namen um `fiatUi.js` als einzige kanonische Logik zu nutzen.

import { isNftCostBasisUnknown } from "./nftCostBasis.js";

import {
  firstFinite,
  fallbackCurrencyFromCountry,
  currencyFromBackend,
  currencySymbol,
  totalsView,
  rowUnitPrice,
  rowTotal,
  rowBuy,
  rowSell,
  rowProfit,
  rowTaxable,
  rowNonTaxable,
} from "./fiatUi.js";

export {
  firstFinite,
  totalsView,
  rowUnitPrice,
  rowTotal,
  rowBuy,
  rowSell,
  rowTaxable,
  rowNonTaxable,
};

export const countryFallbackCurrency = fallbackCurrencyFromCountry;
export const backendCurrencyOrFallback = currencyFromBackend;
export const currencySymbolOf = currencySymbol;

export const pickFiatPrice = rowUnitPrice;
export const pickFiatTotal = rowTotal;
export const pickFiatBuy = rowBuy;
export const pickFiatSell = rowSell;
export const pickFiatGrossProfit = rowProfit;
export const pickFiatTaxable = rowTaxable;
export const pickFiatNonTaxable = rowNonTaxable;

/** Spaltenlabel aus EUR-Übersetzungsschlüssel auf tatsächliche Fiat-Währung mappen. */
export function fiatColumnLabelFromEurKey(key, fiatCurrency, language, translate) {
  const label = translate(key, language);
  const cur = String(fiatCurrency || "EUR").toUpperCase();
  if (cur === "USD") {
    return label.replace(/€/g, "$").replace(/EUR/gi, "USD");
  }
  if (cur === "GBP") {
    return label.replace(/€/g, "£").replace(/EUR/gi, "GBP");
  }
  if (cur === "CHF") {
    return label.replace(/€/g, "CHF").replace(/EUR/gi, "CHF");
  }
  return label;
}

/** Dataset aus verschiedenen Backend-/Snapshot-Formen (analysisResult, snapshot, flach). */
export function resolveAnalysisDataset(data = null) {
  if (!data || typeof data !== "object") return null;

  return (
    data.dataset ||
    data.analysisResult?.dataset ||
    data.snapshot?.dataset ||
    data.processedData ||
    data.analysisResult?.processedData ||
    null
  );
}

/** Steuerland aus Analyse-/Snapshot-Payload (Meta hat Vorrang vor Dataset-Status). */
export function resolveAnalysisCountry(data = null, fallback = "DE") {
  if (!data || typeof data !== "object") {
    return String(fallback || "DE").toUpperCase();
  }

  const root = data?.analysisResult ?? data;
  const ds =
    root?.dataset ||
    data?.dataset ||
    data?.processedData ||
    root?.processedData ||
    {};

  const code = String(
    root?.meta?.countryCode ||
      root?.meta?.country ||
      data?.meta?.countryCode ||
      data?.meta?.country ||
      root?.params?.selectedCountry ||
      root?.params?.countryCode ||
      data?.params?.selectedCountry ||
      root?.selectedCountry ||
      data?.selectedCountry ||
      ds?.countryCode ||
      ds?.country ||
      ds?.selectedCountry ||
      fallback ||
      "DE"
  ).toUpperCase();

  return code || "DE";
}

export const pickSectionTotals = (totals = {}, selectedCountry = "DE") => {
  const v = totalsView(totals, selectedCountry);
  return {
    currency: v.currency,
    disposalNet: v.swapProfit,
    disposalGain: v.swapGain,
    disposalLoss: v.swapLoss,
    otherIncome: v.otherIncome,
    totalTaxable: v.totalTaxable,
    disposalShortTermNet: v.shortTerm,
    disposalLongTermNet: v.longTerm,
  };
};

export const pickFiatShort = (row = {}) =>
  firstFinite(
    row.shortTermProfitFiat,
    row.shortTermNetFiat,
    row.shortTermProfitEUR,
    row.disposalShortTermNetFiat,
    row.disposalShortTermNetEUR
  );

export const pickFiatLong = (row = {}) =>
  firstFinite(
    row.longTermProfitFiat,
    row.longTermNetFiat,
    row.longTermProfitEUR,
    row.disposalLongTermNetFiat,
    row.disposalLongTermNetEUR
  );

/** Zeilen, für die keine finale Steuerzahl dargestellt werden darf (FIFO-Mangel, fehlender Einstand). */
export function isBlockedTaxRow(row = {}) {
  if (row == null || typeof row !== "object") return false;

  return Boolean(
    row.isTaxComputationBlocked ||
      row.taxComputationSkipped ||
      row.fifoShortage ||
      row.missingCostBasis ||
      row.costBasisUnknown ||
      isNftCostBasisUnknown(row) ||
      row.displayPriceStatus === "FIFO_SHORTAGE" ||
      row.displayPriceStatus === "COST_BASIS_UNKNOWN" ||
      row.pricingStatus === "fifo_quantity_shortage" ||
      row.pricingStatus === "cost_basis_unknown" ||
      row.taxResolutionStatus === "needs_cost_basis_review" ||
      row.taxResolutionStatus === "needs_receipt_allocation" ||
      row.displayPriceStatus === "SALE_ALLOCATION_REQUIRED" ||
      String(row.classification || row.type || "").toLowerCase() ===
        "nft_marketplace_payout_review",
  );
}

export function blockedTaxLabel(row = {}, lang = "de") {
  if (row == null || typeof row !== "object") {
    return lang === "en" ? "Excluded from tax totals" : "Aus Steuer-Summe ausgeschlossen";
  }

  const en = lang === "en";
  const reviewReason = String(
    row.reviewReason || row.taxReviewReason || ""
  ).toUpperCase();

  if (
    reviewReason === "NFT_TRANSFORM_TOTAL_VALUATION_MISSING" ||
    reviewReason === "NFT_TRANSFORM_TOTAL_FMV_MISSING"
  ) {
    return en
      ? "Market value at exchange time required"
      : "Marktwert am Tauschzeitpunkt erforderlich";
  }

  if (reviewReason === "NFT_TRANSFORM_RESULT_ACQUISITION_BASIS_MISSING") {
    return en
      ? "New NFT cost basis requires transferred market value"
      : "Neue NFT-Anschaffungskosten: Marktwert der hingegebenen NFTs erforderlich";
  }

  if (
    reviewReason ===
    "NFT_TRANSFORM_SOURCE_ALLOCATION_REQUIRED_MIXED_TAX_STATUS"
  ) {
    return en
      ? "Source NFT valuation allocation required"
      : "Einzelbewertung der eingesetzten NFTs erforderlich";
  }

  if (reviewReason === "NFT_TRANSFORM_BOTH_SIDES_FMV_MISSING") {
    return en
      ? "Market value missing on both sides"
      : "Marktwert beider Tauschseiten fehlt";
  }

  if (reviewReason === "NFT_TRANSFORM_TERM_ALLOCATION_REQUIRED") {
    return en
      ? "Short-/long-term allocation required"
      : "Aufteilung auf Short-/Long-Term erforderlich";
  }

  if (reviewReason === "NFT_TRANSFORM_SOURCE_FMV_ALLOCATION_MISSING") {
    return en
      ? "NFT value allocation required"
      : "Aufteilung der NFT-Marktwerte fehlt";
  }

  if (reviewReason === "NFT_TRANSFORM_OVERRIDE_CURRENCY_MISMATCH") {
    return en
      ? "Valuation currency check required"
      : "Bewertungswährung prüfen";
  }

  if (
    reviewReason.startsWith("NFT_TRANSFORM_") &&
    (
      reviewReason.includes("_FMV_") ||
      reviewReason.endsWith("_FMV_MISSING") ||
      reviewReason.endsWith("_FMV_INVALID") ||
      reviewReason.includes("VALUATION")
    )
  ) {
    return en ? "Valuation required" : "Bewertung erforderlich";
  }

  if (
    reviewReason === "NFT_TRANSFORM_SOURCE_COST_BASIS_UNKNOWN" ||
    reviewReason === "NFT_TRANSFORM_SOURCE_BASIS_DEPENDS_ON_PRIOR_TRANSFORM_VALUATION" ||
    reviewReason === "NFT_TRANSFORM_SOURCE_ACQUISITIONS_INCOMPLETE" ||
    reviewReason === "NFT_TRANSFORM_SOURCE_ACQUISITION_DATE_UNKNOWN"
  ) {
    if (
      reviewReason ===
      "NFT_TRANSFORM_SOURCE_BASIS_DEPENDS_ON_PRIOR_TRANSFORM_VALUATION"
    ) {
      return en
        ? "Prior transform valuation required first"
        : "Vorheriger Transform muss zuerst bewertet werden";
    }
    return en ? "Acquisition costs missing" : "Anschaffungskosten fehlen";
  }

  if (
    row.fifoShortage ||
    row.displayPriceStatus === "FIFO_SHORTAGE" ||
    row.pricingStatus === "fifo_quantity_shortage"
  ) {
    return en ? "FIFO shortage" : "FIFO-Mangel";
  }
  if (
    row.displayPriceStatus === "SALE_ALLOCATION_REQUIRED" ||
    row.taxResolutionStatus === "needs_receipt_allocation" ||
    row.requiresReceiptAllocation ||
    String(row.classification || row.type || "").toLowerCase() ===
      "nft_marketplace_payout_review"
  ) {
    return en
      ? "Receipt allocation required"
      : "Receipt-Zuordnung erforderlich";
  }
  return en ? "Cost basis missing" : "Einstand unklar";
}
